// A separate database per authenticated operator prevents cross-account replay.
export class QualityQueue {
  constructor(owner) {
    this.owner = String(owner);
    this.memory = new Map();
    this.dropped = 0;
    this.serial = Promise.resolve();
    this.db = new Promise(resolve => {
      if (typeof indexedDB === 'undefined') return resolve(null);
      let request;
      try { request = indexedDB.open(`unicall-quality-${this.owner}`, 1); }
      catch (_) { return resolve(null); }
      request.onupgradeneeded = () => request.result.createObjectStore('events', { keyPath: 'key' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  async transaction(mode, run) {
    const db = await this.db;
    if (!db) return null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction('events', mode);
      const result = run(tx.objectStore('events'));
      tx.oncomplete = () => resolve(result && result.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }

  enqueue(event) {
    const key = `${event.kind}:${event.id}:${event.revision}`;
    const item = { key, event, at: Date.now(), bytes: JSON.stringify(event).length * 2 };
    this.serial = this.serial.catch(() => {}).then(async () => {
      try {
        if (await this.db) await this.transaction('readwrite', store => store.put(item));
        else this.memory.set(key, item);
      } catch (_) {
        this.memory.set(key, item);
      }
      await this.prune();
    });
    return this.serial;
  }

  async all() {
    let persisted = [];
    try { persisted = await this.transaction('readonly', store => store.getAll()) || []; } catch (_) { /* memory fallback */ }
    const items = new Map(persisted.map(item => [item.key, item]));
    this.memory.forEach((item, key) => items.set(key, item));
    return [...items.values()].sort((a, b) => a.at - b.at || a.key.localeCompare(b.key));
  }

  async remove(keys) {
    try { await this.transaction('readwrite', store => { keys.forEach(key => store.delete(key)); }); }
    catch (_) { /* Keep persisted records for a later retry; memory still stays bounded. */ }
    keys.forEach(key => this.memory.delete(key));
  }

  async prune() {
    const rows = await this.all();
    let bytes = rows.reduce((sum, row) => sum + row.bytes, 0);
    const keys = [];
    rows.forEach(row => {
      if (row.at < Date.now() - 86400000 || bytes > 5 * 1024 * 1024) {
        keys.push(row.key);
        bytes -= row.bytes;
      }
    });
    if (keys.length) {
      await this.remove(keys);
      this.dropped += keys.length;
    }
  }

  async batch() {
    await this.serial;
    await this.prune();
    return (await this.all()).slice(0, 20);
  }
}
